import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import "./Policy.css";
import { CONFIG } from "./config";

const App: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showComplaintForm, setShowComplaintForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    requestType: 'data_deletion',
    message: '',
    userId: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const toggleSection = (section: string) => {
    if (activeSection === section) {
      setActiveSection(null);
    } else {
      setActiveSection(section);
    }
  };

  // Function to handle quick link clicks
  const handleQuickLinkClick = (sectionId: string, e: React.MouseEvent) => {
    e.preventDefault();
    setActiveSection(sectionId);
    setIsMobileMenuOpen(false);
    
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  // Expand section if URL has a hash on component mount
  useEffect(() => {
    const hash = window.location.hash.substring(1);
    if (hash) {
      setActiveSection(hash);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

 const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch(`${CONFIG.apiEndpoint}/requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          requestType: 'data_deletion',
          message: '',
          userId: ''
        });
        
        setTimeout(() => {
          setSubmitStatus('idle');
          setShowComplaintForm(false);
        }, 5000);
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="policy-wrapper">
      {/* Header */}
      <motion.header
        className="policy-header"
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="header-content">
          <div className="logo-container">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="heart-logo"
            >
              <path d="M20.8 4.6c-1.8-1.8-4.7-1.8-6.5 0l-.3.3-.3-.3c-1.8-1.8-4.7-1.8-6.5 0s-1.8 4.7 0 6.5l6.8 6.8 6.8-6.8c1.8-1.8 1.8-4.7 0-6.5z"></path>
            </svg>
            <h1 className="app-name">{CONFIG.appName}</h1>
          </div>
          <h2>Privacy Policy</h2>
        </div>
      </motion.header>

      {/* Quick Navigation */}
      <nav className="policy-nav">
        <div className="nav-container">
          <div className="nav-header">
            <h3>Quick Links</h3>
            <button 
              className={`hamburger ${isMobileMenuOpen ? 'active' : ''}`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
          <div className={`nav-links ${isMobileMenuOpen ? 'active' : ''}`}>
            <a href="#information-collected" onClick={(e) => handleQuickLinkClick("information-collected", e)}>
              Information Collected
            </a>
            <a href="#data-usage" onClick={(e) => handleQuickLinkClick("data-usage", e)}>
              Data Usage
            </a>
            <a href="#data-sharing" onClick={(e) => handleQuickLinkClick("data-sharing", e)}>
              Data Sharing
            </a>
            <a href="#cookies" onClick={(e) => handleQuickLinkClick("cookies", e)}>
              Cookies
            </a>
            <a href="#rights" onClick={(e) => handleQuickLinkClick("rights", e)}>
              Your Rights
            </a>
            <a href="#contact" onClick={(e) => {
              e.preventDefault();
              const element = document.getElementById('contact');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
              }
              setIsMobileMenuOpen(false);
            }}>
              Contact & Requests
            </a>
          </div>
        </div>
      </nav>

      {/* Content */}
      <motion.main
        className="policy-content"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.8 }}
      >
        <section className="intro">
          <h3>Introduction</h3>
          <p>
            At Panchal Sonar Matrimony, we are committed to protecting your
            privacy and ensuring you have a positive experience on our platform.
            This Privacy Policy outlines how we collect, use, disclose, and
            safeguard your information when you use our matrimony services.
          </p>
          <p>
            By registering with Panchal Sonar Matrimony, you consent to the
            collection, storage, and processing of your personal information as
            described in this policy.
          </p>
        </section>

        <section id="information-collected">
          <h3 onClick={() => toggleSection("information-collected")}>
            1. Information We Collect
            <span
              className={`toggle-arrow ${
                activeSection === "information-collected" ? "expanded" : ""
              }`}
            >
              ▼
            </span>
          </h3>
          {activeSection === "information-collected" && (
            <div className="section-details">
              <h4>Personal Information Provided by You</h4>
              <ul>
                <li>
                  <strong>Profile Information:</strong> Name, gender, date of
                  birth, marital status, height, weight, physical disability (if
                  any), family details, photos, and horoscope
                </li>
                <li>
                  <strong>Contact Information:</strong> Email address, phone
                  number, social media profiles, residential address
                </li>
                <li>
                  <strong>Professional & Educational Information:</strong>{" "}
                  Education qualifications, college/institution, occupation,
                  employment details, annual income
                </li>
                <li>
                  <strong>Lifestyle Information:</strong> Dietary habits,
                  drinking/smoking habits, family values, partner expectations
                </li>
              </ul>

              <h4>Information Collected Automatically</h4>
              <ul>
                <li>
                  <strong>Technical Information:</strong> IP address, browser
                  type, device information, operating system
                </li>
                <li>
                  <strong>Usage Information:</strong> Pages visited, features
                  used, time spent on the platform, search history
                </li>
                <li>
                  <strong>Location Information:</strong> General location based
                  on IP address or precise location with your consent
                </li>
              </ul>
            </div>
          )}
        </section>

        <section id="data-usage">
          <h3 onClick={() => toggleSection("data-usage")}>
            2. How We Use Your Information
            <span
              className={`toggle-arrow ${
                activeSection === "data-usage" ? "expanded" : ""
              }`}
            >
              ▼
            </span>
          </h3>
          {activeSection === "data-usage" && (
            <div className="section-details">
              <p>
                We use your personal information for the following purposes:
              </p>
              <ul>
                <li>To create and manage your matrimony profile</li>
                <li>
                  To match you with potential partners based on your preferences
                </li>
                <li>To facilitate communication between interested members</li>
                <li>
                  To verify your identity and prevent fraudulent activities
                </li>
                <li>
                  To provide customer support and respond to your inquiries
                </li>
                <li>To send you service-related notifications and updates</li>
                <li>
                  To conduct research and analysis to improve our services
                </li>
                <li>
                  To display personalized content and targeted advertisements
                </li>
                <li>
                  To enforce our Terms of Service and community guidelines
                </li>
              </ul>
            </div>
          )}
        </section>

        <section id="visibility">
          <h3 onClick={() => toggleSection("visibility")}>
            3. Visibility of Your Information
            <span
              className={`toggle-arrow ${
                activeSection === "visibility" ? "expanded" : ""
              }`}
            >
              ▼
            </span>
          </h3>
          {activeSection === "visibility" && (
            <div className="section-details">
              <p>
                Your profile information is displayed to other registered
                members based on the following visibility settings:
              </p>

              <h4>Publicly Visible Information</h4>
              <ul>
                <li>First name, age, height, education, profession</li>
                <li>
                  Photos (unless you choose to hide them from non-premium
                  members)
                </li>
                <li>Family background and lifestyle information</li>
              </ul>

              <h4>Contact Information Visibility</h4>
              <ul>
                <li>
                  Your contact details (phone number, email address) are never
                  displayed publicly
                </li>
                <li>
                  Premium members can request contact information after your
                  approval
                </li>
                <li>
                  You can control who sees your contact information through
                  privacy settings
                </li>
              </ul>

              <p className="warning-note">
                <strong>Important:</strong> While we provide privacy controls,
                please be aware that any information you share on your profile
                may be copied or stored by other users.
              </p>
            </div>
          )}
        </section>

        <section id="data-sharing">
          <h3 onClick={() => toggleSection("data-sharing")}>
            4. Information Sharing and Disclosure
            <span
              className={`toggle-arrow ${
                activeSection === "data-sharing" ? "expanded" : ""
              }`}
            >
              ▼
            </span>
          </h3>
          {activeSection === "data-sharing" && (
            <div className="section-details">
              <p>
                We may share your information in the following circumstances:
              </p>

              <h4>With Other Users</h4>
              <p>
                Your profile information is shared with other registered users
                as per your privacy settings to facilitate matches.
              </p>

              <h4>With Service Providers</h4>
              <p>
                We engage third-party companies to perform services such as:
              </p>
              <ul>
                <li>Payment processing</li>
                <li>Data analysis and analytics</li>
                <li>Marketing and advertising</li>
                <li>Customer support</li>
                <li>Background verification</li>
              </ul>
            </div>
          )}
        </section>

        <section id="cookies">
          <h3 onClick={() => toggleSection("cookies")}>
            5. Cookies and Tracking Technologies
            <span
              className={`toggle-arrow ${
                activeSection === "cookies" ? "expanded" : ""
              }`}
            >
              ▼
            </span>
          </h3>
          {activeSection === "cookies" && (
            <div className="section-details">
              <p>We use cookies and similar tracking technologies to:</p>
              <ul>
                <li>Remember your preferences and settings</li>
                <li>Analyze site traffic and usage patterns</li>
                <li>Deliver personalized advertisements</li>
                <li>Provide social media features</li>
                <li>Improve our services and user experience</li>
              </ul>
            </div>
          )}
        </section>

        <section id="data-security">
          <h3 onClick={() => toggleSection("data-security")}>
            6. Data Security
            <span
              className={`toggle-arrow ${
                activeSection === "data-security" ? "expanded" : ""
              }`}
            >
              ▼
            </span>
          </h3>
          {activeSection === "data-security" && (
            <div className="section-details">
              <p>
                We implement appropriate technical and organizational measures
                to protect your personal information, including:
              </p>
              <ul>
                <li>SSL encryption for data transmission</li>
                <li>Secure servers with restricted access</li>
                <li>Regular security assessments and testing</li>
                <li>Employee training on data protection</li>
                <li>Strict access controls to personal information</li>
              </ul>
            </div>
          )}
        </section>

        <section id="rights">
          <h3 onClick={() => toggleSection("rights")}>
            7. Your Rights
            <span
              className={`toggle-arrow ${
                activeSection === "rights" ? "expanded" : ""
              }`}
            >
              ▼
            </span>
          </h3>
          {activeSection === "rights" && (
            <div className="section-details">
              <p>
                Depending on your location, you may have the following rights
                regarding your personal information:
              </p>

              <ul>
                <li>
                  <strong>Access:</strong> Request a copy of the personal
                  information we hold about you
                </li>
                <li>
                  <strong>Correction:</strong> Update or correct inaccurate
                  information in your profile
                </li>
                <li>
                  <strong>Deletion:</strong> Request deletion of your personal
                  information
                </li>
                <li>
                  <strong>Restriction:</strong> Request temporary restriction of
                  processing of your information
                </li>
                <li>
                  <strong>Data Portability:</strong> Request transfer of your
                  data to another service provider
                </li>
                <li>
                  <strong>Objection:</strong> Object to processing of your
                  information for certain purposes
                </li>
              </ul>
            </div>
          )}
        </section>

       <section id="contact">
          <h3>8. Contact Us & Data Requests</h3>
          <p>
            If you have any questions, concerns, or requests regarding this
            Privacy Policy or our data practices, please contact us:
          </p>
          <div className="contact-details">
            <p>
              <strong>Email:</strong> {CONFIG.contactEmail}
            </p>
            <p>
              <strong>Phone:</strong> {CONFIG.contactPhone}
            </p>
            <p>
              <strong>Address:</strong> {CONFIG.contactAddress}
            </p>
          </div>

          <div className="complaint-section">
            <button 
              className="complaint-toggle-btn"
              onClick={() => setShowComplaintForm(!showComplaintForm)}
            >
              {showComplaintForm ? 'Hide Request Form' : 'Submit Data Request or Complaint'}
            </button>

            {showComplaintForm && (
              <motion.div 
                className="complaint-form"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                transition={{ duration: 0.3 }}
              >
                <h4>Submit Your Request</h4>
    <p>
      Use this form to request data deletion, report issues, or ask questions about your data.
      We will process your request within {CONFIG.processingTime} after submission.
    </p>
                
                {submitStatus === 'success' && (
                  <div className="alert alert-success">
                    Your request has been submitted successfully. We'll respond as soon as possible.
                  </div>
                )}
                
                {submitStatus === 'error' && (
                  <div className="alert alert-error">
                    There was an error submitting your request. Please try again or contact us directly via email.
                  </div>
                )}
                
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="name">Your Name <span>*</span></label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">Your Email <span>*</span></label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="requestType">Request Type <span>*</span></label>
                    <select
                      id="requestType"
                      name="requestType"
                      value={formData.requestType}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="data_deletion">Data Deletion Request</option>
                      {/* <option value="data_access">Data Access Request</option> */}
                      <option value="complaint">Complaint</option>
                      <option value="question">General Question</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  
                  {formData.requestType === 'data_deletion' && (
                    <div className="form-group">
                      <label htmlFor="userId">User ID/Email associated with your account <span>*</span></label>
                      <input
                        type="text"
                        id="userId"
                        name="userId"
                        value={formData.userId}
                        onChange={handleInputChange}
                        required={formData.requestType === 'data_deletion'}
                        placeholder="Please provide the email you used to register"
                      />
                    </div>
                  )}
                  
                  <div className="form-group">
                    <label htmlFor="message">Message <span>*</span></label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={5}
                      required
                      placeholder="Please provide details about your request..."
                    ></textarea>
                  </div>
                  
                  <button 
                    type="submit" 
                    className="submit-btn"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Request'}
                  </button>
                </form>
              </motion.div>
            )}
          </div>
        </section>
      </motion.main>

      {/* Footer */}
      <motion.footer
        className="policy-footer"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.6 }}
      >
        <div className="footer-content">
          <div className="footer-logo-container">
            <img
              src="https://codeimplants.com/assets/Image/Black%20Logo%20.png"
              alt="Code Implants Logo"
              className="footer-logo"
            />
            <p>
              © {new Date().getFullYear()}{" "}
              <a
                href="https://codeimplants.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                Code Implants Software Technologies Pvt. Ltd.
              </a>
            </p>
          </div>
        </div>
      </motion.footer>
    </div>
  );
};

export default App;